import {reactive} from 'vue'
import {send} from '@/composables/apiClient'

// Server-driven map tile + geocoding endpoints.
//
// Every tile and geocoding URL comes from the API (sourced from its .env),
// delivered by the public GET /auth/map-config endpoint (and inline on login).
// The web portal holds NO such URLs of its own - templates bind to the global
// $mapTiles and the geocode composables import `mapTiles` from here.
//
// NOTE (web): browsers do not allow setting a custom User-Agent on fetch/axios/
// img, so there is no client identity to configure here - only the URLs. The
// web portal identifies itself to the hosts through the browser Referer/Origin
// (the domain), which only the server-side proxy (a later phase) can remove.
//
// The BOOTSTRAP values are a last-resort only, used before the first server
// block arrives (or if it is unreachable). They mirror today's endpoints so a
// missing/unreachable config is a no-op; they are NOT the source of truth.
const LS_KEY = 'map_config_v1'

const BOOTSTRAP = {
    street: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    satelliteLabels: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    topo: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    reverseUrl: 'https://nominatim.openstreetmap.org/reverse',
    searchUrl: 'https://nominatim.openstreetmap.org/search',
}

// Map a raw server block (the GET /auth/map-config `data`) to our flat shape.
// Returns {} for anything missing or when the server kill-switch is off, so
// callers keep the bootstrap/last-known values.
function normalize(block) {
    if (!block || typeof block !== 'object' || block.enabled === false) return {}
    const t = block.tiles || {}
    const g = block.geocode || {}
    const out = {}
    if (t.street && t.street.url) out.street = t.street.url
    if (t.satellite && t.satellite.url) out.satellite = t.satellite.url
    if (t.satellite_labels && t.satellite_labels.url) out.satelliteLabels = t.satellite_labels.url
    if (t.topo && t.topo.url) out.topo = t.topo.url
    if (g.reverse_url) out.reverseUrl = g.reverse_url
    if (g.search_url) out.searchUrl = g.search_url
    return out
}

function readCached() {
    try {
        const raw = localStorage.getItem(LS_KEY)
        return raw ? normalize(JSON.parse(raw)) : {}
    } catch {
        return {}
    }
}

// Reactive so templates bound to $mapTiles pick up a changed endpoint. Seeded
// with the bootstrap overlaid by the last-known server block from localStorage.
export const mapTiles = reactive({...BOOTSTRAP, ...readCached()})

// Fetch the latest block once at startup and persist it. Never throws.
export async function initMapConfig() {
    try {
        const res = await send('get', 'auth/map-config')
        const block = res && res.data ? res.data.data : null
        if (block) {
            Object.assign(mapTiles, normalize(block))
            try {
                localStorage.setItem(LS_KEY, JSON.stringify(block))
            } catch { /* quota - ignore */ }
        }
    } catch { /* keep bootstrap / last-known */ }
}
